import posthog from 'posthog-js';

import { getConfig } from '../config';
import {
  type OrganizationBilling,
  type PlanKind,
  type PlanStatus,
} from '../entity/api/organization-billing';
import { type User } from '../entity/api/user';
import { trackError } from '../error/tracking';
import { getLogger } from '../logger';
import { captureException } from '../logger/capture-exception';

const logger = getLogger();
const isDevelopment =
  getConfig().env === 'testing' ||
  getConfig().env === 'development' ||
  getConfig().appDomain.indexOf('localhost') !== -1;

let cachedIdWorkspace = '';
let cachedIdUser = '';

/**
 * PostHog Analytics Service
 *
 * A non-blocking, safe wrapper around PostHog event tracking.
 * All methods are wrapped in try-catch to ensure tracking failures
 * never impact the application.
 */

/**
 * Initialize PostHog with autocapture restricted to known app domains and
 * elements explicitly tagged with data-ph-capture.
 *
 * @see https://posthog.com/docs/libraries/js/autocapture
 */
export function initPostHog(): void {
  if (isDevelopment) {
    return;
  }

  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string, {
    // eslint-disable-next-line camelcase
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string,
    defaults: '2025-11-30',
    autocapture: {
      // Only autocapture on production and staging domains
      // eslint-disable-next-line camelcase
      url_allowlist: ['https://app\\.blym\\.co', 'https://app-staging\\.blym\\.co'],
      // url_allowlist: ['http://localhost'],
      // Only capture elements explicitly opted-in via data-ph-capture attribute
      // eslint-disable-next-line camelcase
      css_selector_allowlist: ['[data-ph-capture]'],
    },
    // Disable automatic pageview tracking; we handle navigation events manually if needed
    // @see https://posthog.com/docs/libraries/js/pageviews
    // eslint-disable-next-line camelcase
    capture_pageview: false,
    // Recording is opt-in per route — started explicitly via startPostHogSessionRecording()
    // @see https://posthog.com/docs/session-replay/how-to-control-which-sessions-you-record#programmatically-start-and-stop-recordings
    // eslint-disable-next-line camelcase
    disable_session_recording: true,
  });
}

/**
 * Check if PostHog is initialized and ready to capture events.
 */
function isPostHogReady(): boolean {
  if (isDevelopment) {
    return false;
  }

  try {
    // Check if posthog is initialized and has a valid session
    return posthog != null && typeof posthog.capture === 'function';
  } catch {
    return false;
  }
}

/**
 * Start a session recording for the current user.
 * Call this when entering a key product flow (content generation, onboarding, etc.).
 *
 * @see https://posthog.com/docs/session-replay/how-to-control-which-sessions-you-record#programmatically-start-and-stop-recordings
 */
export function startPostHogSessionRecording(): void {
  try {
    if (!isPostHogReady()) {
      return;
    }

    posthog.startSessionRecording();
    logger.info('[PostHog] session recording started');
  } catch (err) {
    captureException(
      err,
      {
        file: 'posthog.ts',
        method: 'startPostHogSessionRecording',
        infoMessage: 'Failed to start PostHog session recording',
      },
      trackError
    );
  }
}

/**
 * Stop the active session recording.
 * Call this when leaving a key product flow.
 */
export function stopPostHogSessionRecording(): void {
  try {
    if (!isPostHogReady()) {
      return;
    }

    posthog.stopSessionRecording();
    logger.info('[PostHog] session recording stopped');
  } catch (err) {
    captureException(
      err,
      {
        file: 'posthog.ts',
        method: 'stopPostHogSessionRecording',
        infoMessage: 'Failed to stop PostHog session recording',
      },
      trackError
    );
  }
}

// List of events to captures
export const PostHogEvents = {
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_ORIGIN: 'onboarding_origin',
  ONBOARDING_STEP1_COMPLETED: 'onboarding_step1_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  CONTENT_VIEWED: 'content_viewed',
  EXPORT_CONTENT: 'export_content',
  CONTENT_CREATED: 'content_created',
  CONTENT_REWRITTEN: 'content_rewritten',
  CONTENT_GRADED: 'content_graded',
  BROONO_USED: 'broono_used',
  SEO_AUDIT_RUN: 'seo_audit_run',
  MEMBER_INVITED: 'member_invited',
  SIGNUP_COMPLETED: 'signup_completed',
  FUNNEL_OPENED: 'funnel_opened',
  FUNNEL_STEP1_COMPLETED: 'funnel_step1_completed',
  FUNNEL_STEP2_COMPLETED: 'funnel_step2_completed',
  FUNNEL_CONTENT_GENERATING: 'funnel_content_generating',
} as const;

export type PostHogEventName = (typeof PostHogEvents)[keyof typeof PostHogEvents];

// Generic event properties type
export type PostHogEventProperties = Record<string, unknown>;

/**
 * Safely capture a PostHog event.
 * This function is non-blocking and will never throw.
 *
 * @param eventName - The name of the event to capture
 * @param properties - Optional properties to attach to the event
 */
export function capturePostHogEvent(eventName: string, properties?: PostHogEventProperties): void {
  try {
    if (!isPostHogReady()) {
      logger.info('[PostHog] not ready, skipping event:', eventName);
      return;
    }

    posthog.capture(eventName, properties);
    logger.info('[PostHog] event captured:', eventName, properties);
  } catch (err) {
    captureException(
      err,
      {
        file: 'posthog.ts',
        method: 'capturePostHogEvent',
        infoMessage: 'Failed to capture PostHog event',
        eventName,
      },
      trackError
    );
  }
}

/**
 * Associate the current user session with a PostHog workspace group.
 * Should be called whenever the selected organization changes (initial load or user-driven switch).
 *
 * @see https://posthog.com/docs/product-analytics/group-analytics
 */
export function groupPostHogWorkspace(
  organization?: {
    _id: string;
    createdAt: number;
  },
  billing?: {
    currentPlanStatus?: PlanStatus | null;
    plan?: {
      kind?: PlanKind | null;
    } | null;
  }
): void {
  try {
    if (!isPostHogReady()) {
      logger.info('[PostHog] not ready, skipping group');
      return;
    }

    if (organization == null) {
      posthog.group('workspace', 'unknown', {
        // eslint-disable-next-line camelcase
        distinct_id: 'unknown',
      });
      return;
    }

    // check if the cache makes sense
    const newWorkspace = {
      // eslint-disable-next-line camelcase
      distinct_id: organization._id,
      // eslint-disable-next-line camelcase
      created_at: new Date(organization.createdAt).toISOString(),
      plan: billing?.plan?.kind,
      trialStatus: billing?.currentPlanStatus,
    };
    if (cachedIdWorkspace !== JSON.stringify(newWorkspace)) {
      posthog.group('workspace', organization._id, newWorkspace);
      cachedIdWorkspace = JSON.stringify(newWorkspace);

      logger.info('[PostHog] workspace group set:', organization._id);
    }
  } catch (err) {
    captureException(
      err,
      {
        file: 'posthog.ts',
        method: 'groupPostHogWorkspace',
        infoMessage: 'Failed to set PostHog workspace group',
      },
      trackError
    );
  }
}

/**
 * Reset the PostHog session on sign-out so the next user starts with a clean
 * anonymous distinct_id. Without this, events fired between a page load and the
 * next identify() call would be attributed to the previous user.
 *
 * @see https://posthog.com/docs/product-analytics/identify#3-reset-after-logout
 */
export function resetPostHogUser(): void {
  try {
    if (!isPostHogReady()) {
      return;
    }

    posthog.reset();
    cachedIdUser = '';
    cachedIdWorkspace = '';
    logger.info('[PostHog] session reset');
  } catch (err) {
    captureException(
      err,
      {
        file: 'posthog.ts',
        method: 'resetPostHogUser',
        infoMessage: 'Failed to reset PostHog session',
      },
      trackError
    );
  }
}

/**
 * Identify the authenticated user in PostHog.
 * Should be called once when the user is first resolved (login, signup, or app reload).
 *
 * @see https://posthog.com/docs/product-analytics/identify
 */
export function identifyPostHogUser(
  user?: {
    _id: User['_id'];
    firstName: User['firstName'];
    lastName: User['lastName'];
    email: User['email'];
    createdAt: User['createdAt'];
  },
  currentOrganization?: Partial<
    Pick<OrganizationBilling, 'organizationId' | 'currentPlanStatus' | 'plan'>
  >
): void {
  try {
    if (!isPostHogReady()) {
      logger.info('[PostHog] not ready, skipping identify');
      return;
    }

    if (user == null) {
      logger.info('[PostHog] user is undefined, skipping identify');
      return;
    }

    const newUser = {
      kind: 'user',
      name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      email: user.email,
      signupDate: new Date(user.createdAt).toISOString(),
      currOrgId: currentOrganization?.organizationId,
      currOrgTrialStatus: currentOrganization?.currentPlanStatus,
      currOrgPricingPlan: currentOrganization?.plan?.kind,
    };

    if (cachedIdUser !== JSON.stringify(newUser)) {
      posthog.identify(user._id, newUser);
      cachedIdUser = JSON.stringify(newUser);

      logger.info('[PostHog] user identified:', user._id);
    }
  } catch (err) {
    captureException(
      err,
      {
        file: 'posthog.ts',
        method: 'identifyPostHogUser',
        infoMessage: 'Failed to identify PostHog user',
      },
      trackError
    );
  }
}
